import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

import { Label } from './ui/label';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Download, Upload, FileJson, FileSpreadsheet, Database, Users, Building, Briefcase, FileText, Calendar, Shield, AlertTriangle, CheckCircle, RefreshCw, Archive, FolderDown, FolderUp, Clock, Filter, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ExportImportBar from './ExportImportBar';
import ExportHistoryTab from './ExportHistoryTab';

const API = process.env.REACT_APP_BACKEND_URL;

const DATA_TYPES = [
  { id: 'users', label: 'Utilisateurs', icon: Users, description: 'Membres, COOPER\'S, Experts, Admins', color: 'bg-blue-600' },
  { id: 'subscriptions', label: 'Souscriptions', icon: FileText, description: 'Toutes les souscriptions et contrats', color: 'bg-green-600' },
  { id: 'damages', label: 'Dommages', icon: AlertTriangle, description: 'Déclarations de dommages', color: 'bg-red-600' },
  { id: 'conventions', label: 'Conventions', icon: Shield, description: 'Conventions de partenariat', color: 'bg-purple-600' },
  { id: 'agenda', label: 'Agenda', icon: Calendar, description: 'Événements et rendez-vous', color: 'bg-orange-600' },
  { id: 'territories', label: 'Territoires', icon: Building, description: 'Zones géographiques', color: 'bg-cyan-600' },
  { id: 'documents', label: 'Documents', icon: Archive, description: 'Modèles et documents', color: 'bg-pink-600' },
  { id: 'assignments', label: 'Assignations', icon: Briefcase, description: 'Assignations admin-utilisateur', color: 'bg-indigo-600' },
];

const EXPORT_FORMATS = [
  { id: 'json', label: 'JSON', icon: FileJson, description: 'Format structuré, idéal pour import/export' },
  { id: 'csv', label: 'CSV', icon: FileSpreadsheet, description: 'Compatible Excel et tableurs' },
  { id: 'xlsx', label: 'Excel', icon: FileSpreadsheet, description: 'Format Microsoft Excel natif' },
];

export default function ExportImportData({ getAuthHeader }) {
  const [activeTab, setActiveTab] = useState('export');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [exportFormat, setExportFormat] = useState('json');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [genericCollections, setGenericCollections] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/generic/collections`, {
          headers: getAuthHeader(),
        });
        if (!cancelled) setGenericCollections(r.data?.collections || []);
      } catch { /* silencieux */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);
  const [exportHistory, setExportHistory] = useState([
    { id: 1, date: '2024-12-12 14:30', types: ['users', 'subscriptions'], format: 'json', size: '2.4 MB', status: 'success' },
    { id: 2, date: '2024-12-10 09:15', types: ['damages'], format: 'csv', size: '856 KB', status: 'success' },
    { id: 3, date: '2024-12-08 16:45', types: ['all'], format: 'xlsx', size: '5.2 MB', status: 'success' },
  ]);
  const [importHistory, setImportHistory] = useState([
    { id: 1, date: '2024-12-11 11:20', types: ['users'], records: 45, status: 'success' },
    { id: 2, date: '2024-12-09 14:00', types: ['territories'], records: 12, status: 'partial' },
  ]);

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const selectAllTypes = () => {
    setSelectedTypes(DATA_TYPES.map(t => t.id));
  };

  const clearSelection = () => {
    setSelectedTypes([]);
  };

  const handleExport = async () => {
    if (selectedTypes.length === 0) {
      toast.error('Veuillez sélectionner au moins un type de données');
      return;
    }

    setExporting(true);
    setProgress(20);

    try {
      const response = await axios.post(
        `${API}/api/admin/export`,
        { types: selectedTypes, format: exportFormat },
        { 
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          responseType: 'blob',
          timeout: 120000,
          onDownloadProgress: (e) => {
            if (e.total) setProgress(Math.round(20 + (e.loaded / e.total) * 70));
          },
        }
      );

      setProgress(95);

      const blob = new Blob([response.data], { 
        type: exportFormat === 'json' ? 'application/json' : 
              exportFormat === 'csv' ? 'text/csv' : 
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_oscop_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      link.click();
      window.URL.revokeObjectURL(url);

      setProgress(100);
      toast.success('Export réalisé avec succès');
      
      setExportHistory(prev => [{
        id: Date.now(),
        date: new Date().toLocaleString('fr-FR'),
        types: selectedTypes,
        format: exportFormat,
        size: (blob.size / 1024).toFixed(1) + ' KB',
        status: 'success'
      }, ...prev]);

    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Erreur export (${status || 'réseau'}): ${detail}`);
    } finally {
      setExporting(false);
      setProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      
      // Prévisualiser le fichier JSON
      if (file.name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target.result);
            setImportPreview({
              types: Object.keys(data),
              records: Object.values(data).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0),
              size: (file.size / 1024).toFixed(2) + ' KB'
            });
          } catch {
            toast.error('Fichier JSON invalide');
          }
        };
        reader.readAsText(file);
      } else {
        setImportPreview({
          types: ['Détection automatique'],
          records: 'À déterminer',
          size: (file.size / 1024).toFixed(2) + ' KB'
        });
      }
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmImport = async () => {
    setShowConfirmModal(false);
    setImporting(true);
    setProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      setProgress(30);

      const response = await axios.post(
        `${API}/api/admin/import`,
        formData,
        {
          headers: { ...getAuthHeader() },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round(30 + (e.loaded / e.total) * 50));
          },
          timeout: 120000,
        }
      );

      setProgress(100);
      const result = response.data;
      const msg = result.message || 'Import réalisé avec succès';
      if (result.errors > 0) {
        toast.warning(`${msg} (${result.errors} erreur(s))`);
      } else {
        toast.success(msg);
      }
      
      setImportHistory(prev => [{
        id: Date.now(),
        date: new Date().toLocaleString('fr-FR'),
        types: importPreview?.types || ['inconnu'],
        records: (result.updated || 0) + (result.created || 0),
        status: result.errors > 0 ? 'partial' : 'success'
      }, ...prev]);

      setImportFile(null);
      setImportPreview(null);

    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.response?.data?.message || error.message;
      toast.error(`Erreur import (${status || 'réseau'}): ${detail}`);
    } finally {
      setImporting(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Database className="h-6 w-6 text-white" />
            </div>
            Export / Import de Données
          </h2>
          <p className="text-gray-500 mt-1">
            Gérez vos données : exportez pour sauvegarde ou importez depuis un fichier
          </p>
        </div>
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="export" className="flex items-center gap-2">
            <FolderDown className="h-4 w-4" />
            Exporter
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2">
            <FolderUp className="h-4 w-4" />
            Importer
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        {/* Tab Export */}
        <TabsContent value="export" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sélection des types */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5 text-blue-600" />
                    Types de données à exporter
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllTypes}>
                      Tout sélectionner
                    </Button>
                    <Button size="sm" variant="outline" onClick={clearSelection}>
                      Effacer
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {DATA_TYPES.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => toggleType(type.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedTypes.includes(type.id)
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${type.color} flex items-center justify-center`}>
                          <type.icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Checkbox checked={selectedTypes.includes(type.id)} />
                            <span className="font-medium">{type.label}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Options d'export */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-purple-600" />
                  Options d&apos;export
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-2 block">Format de fichier</Label>
                  <div className="space-y-2">
                    {EXPORT_FORMATS.map((format) => (
                      <div
                        key={format.id}
                        onClick={() => setExportFormat(format.id)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          exportFormat === format.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <format.icon className="h-5 w-5 text-purple-600" />
                          <div>
                            <div className="font-medium">{format.label}</div>
                            <div className="text-xs text-gray-500">{format.description}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="text-sm text-gray-600 mb-4">
                    <strong>{selectedTypes.length}</strong> type(s) sélectionné(s)
                  </div>
                  
                  {exporting && (
                    <div className="mb-4">
                      <Progress value={progress} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1 text-center">{progress}%</p>
                    </div>
                  )}

                  <Button 
                    onClick={handleExport} 
                    disabled={exporting || selectedTypes.length === 0}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {exporting ? (
                      <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Export en cours...</>
                    ) : (
                      <><Download className="h-4 w-4 mr-2" /> Exporter les données</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Import */}
        <TabsContent value="import" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Zone de dépôt */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5 text-green-600" />
                  Importer des données
                </CardTitle>
                <CardDescription>
                  Formats acceptés : JSON, CSV, XLSX
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    importFile ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="file"
                    id="import-file"
                    accept=".json,.csv,.xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="import-file" className="cursor-pointer">
                    {importFile ? (
                      <div className="space-y-2">
                        <CheckCircle className="h-12 w-12 mx-auto text-green-600" />
                        <p className="font-medium text-green-700">{importFile.name}</p>
                        <p className="text-sm text-gray-500">Cliquez pour changer de fichier</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <FolderUp className="h-12 w-12 mx-auto text-gray-400" />
                        <p className="font-medium text-gray-700">Glissez un fichier ici</p>
                        <p className="text-sm text-gray-500">ou cliquez pour sélectionner</p>
                      </div>
                    )}
                  </label>
                </div>

                {importing && (
                  <div className="mt-4">
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-gray-500 mt-1 text-center">{progress}%</p>
                  </div>
                )}

                <Button 
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  {importing ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Import en cours...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Importer le fichier</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Prévisualisation */}
            <Card>
              <CardHeader>
                <CardTitle>Prévisualisation</CardTitle>
              </CardHeader>
              <CardContent>
                {importPreview ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Types détectés</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {importPreview.types.map((type, i) => (
                              <Badge key={i} className="bg-blue-700 text-white font-medium">{type}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Enregistrements</p>
                          <p className="text-xl font-bold text-blue-700">{importPreview.records}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Taille du fichier</p>
                          <p className="font-medium">{importPreview.size}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-yellow-800">Attention</p>
                          <p className="text-sm text-yellow-700">
                            L&apos;import peut écraser des données existantes. 
                            Assurez-vous d&apos;avoir une sauvegarde.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileJson className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Sélectionnez un fichier pour voir la prévisualisation</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Historique */}
        <TabsContent value="history" className="space-y-6">
          <ExportHistoryTab exportHistory={exportHistory} importHistory={importHistory} />
        </TabsContent>
      </Tabs>

      {/* Section "Collections additionnelles" — export/import par collection (whitelist générique) */}
      <Card className="border-slate-200" data-testid="generic-collections-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-indigo-600" />
            Collections additionnelles (export/import direct)
          </CardTitle>
          <CardDescription>
            Pour chaque collection ci-dessous, vous pouvez télécharger un export CSV/JSON ou importer un fichier directement. Idéal pour migrer, sauvegarder ou pré-remplir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {genericCollections.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune collection disponible.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {genericCollections.map((c) => (
                <div
                  key={c.slug}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid={`generic-collection-${c.slug}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{c.label}</p>
                    <p className="text-xs font-mono text-slate-500 truncate">{c.slug}</p>
                  </div>
                  <ExportImportBar collection={c.slug} label={c.label} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmation import */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Confirmer l&apos;import
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Vous êtes sur le point d&apos;importer des données. Cette action peut modifier 
              ou écraser des données existantes.
            </p>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm"><strong>Fichier:</strong> {importFile?.name}</p>
              {importPreview && (
                <>
                  <p className="text-sm"><strong>Types:</strong> {importPreview.types.join(', ')}</p>
                  <p className="text-sm"><strong>Enregistrements:</strong> {importPreview.records}</p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Annuler
            </Button>
            <Button onClick={confirmImport} className="bg-green-600 hover:bg-green-700">
              Confirmer l&apos;import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
